import { useState, useEffect } from 'react';
import { typedApi } from '../../api/typed';
import { Sparkles } from 'lucide-react';
import { AnimatedNumber } from '../common/AnimatedNumber';

const SHLOKAS = [
  { sanskrit: 'नियतं कुरु कर्म त्वम्', meaning: 'Perform your duties with discipline.' },
  { sanskrit: 'योगः कर्मसु कौशलम्', meaning: 'Excellence in action is yoga.' },
  { sanskrit: 'उद्यमेन हि सिध्यन्ति कार्याणि', meaning: 'Tasks succeed through effort alone.' },
  { sanskrit: 'विद्या ददाति विनयम्', meaning: 'Knowledge gives humility.' },
  { sanskrit: 'धर्मो रक्षति रक्षितः', meaning: 'Dharma protects those who protect it.' },
  { sanskrit: 'सत्यमेव जयते', meaning: 'Truth alone triumphs.' },
  { sanskrit: 'कर्मण्येवाधिकारस्ते', meaning: 'Your right is to work alone.' },
  { sanskrit: 'श्रद्धावान् लभते ज्ञानम्', meaning: 'The faithful attain wisdom.' },
  { sanskrit: 'अलसस्य कुतो विद्या', meaning: 'Where is knowledge for the lazy?' },
  { sanskrit: 'सुखार्थिनः कुतो विद्या', meaning: 'Where is learning for the comfort-seeker?' },
  { sanskrit: 'आत्मनो मोक्षार्थं जगद्धिताय च', meaning: 'For self-liberation and the welfare of the world.' },
  { sanskrit: 'वसुधैव कुटुम्बकम्', meaning: 'The world is one family.' },
  { sanskrit: 'परोपकारः पुण्याय', meaning: 'Service to others is the highest virtue.' },
  { sanskrit: 'अभ्यासेन तु कौन्तेय', meaning: 'Through practice, Arjuna, mastery comes.' },
];

/**
 * Sidebar Daily Brief — counts (tasks · calls · due) + a daily Sanskrit
 * shloka with its English meaning. No top-priorities list (user-trimmed).
 */
export function DailyBriefCard() {
  const [brief, setBrief] = useState(null);
  const todayShloka = SHLOKAS[new Date().getDate() % SHLOKAS.length];

  useEffect(() => {
    typedApi.get('/api/daily-brief').then(setBrief).catch(() => {});
  }, []);

  const dueCount = brief ? (brief.overduePayments || brief.paymentsDueToday || 0) : 0;
  const dueLabel = brief?.overduePayments > 0 ? 'Overdue' : 'Due';  return (
    <div className="rounded-2xl px-3 py-3 pd-glass" data-testid="sidebar-assistant-card">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-3.5 h-3.5" style={{ color: 'var(--pd-nav-accent)' }} />
        <span
          className="text-[10px] font-semibold uppercase tracking-[0.12em]"
          style={{ color: 'var(--pd-subtle)' }}
        >
          Daily brief
        </span>
      </div>

      {brief ? (
        <div className="flex gap-2 mb-2" data-testid="daily-brief-counts">
          <CountTile label="Tasks" value={brief.taskCount} />
          <CountTile label="Calls" value={brief.callCount} />
          <CountTile
            label={brief?.overduePayments > 0 ? 'Overdue' : 'Due'}
            value={dueCount}
            danger={brief.overduePayments > 0}
          />
        </div>
      ) : null}

      <div style={{ borderTop: brief ? '1px solid var(--pd-border-subtle)' : 'none', paddingTop: brief ? 6 : 0 }}>
        <p
          data-testid="daily-shloka-sanskrit"
          className="text-[12px] leading-relaxed"
          style={{ color: 'var(--pd-text)' }}
        >
          {todayShloka.sanskrit}
        </p>
        <p
          data-testid="daily-shloka-meaning"
          className="text-[10px] mt-1 italic"
          style={{ color: 'var(--pd-subtle)', lineHeight: 1.6 }}
        >
          {todayShloka.meaning}
        </p>
      </div>
    </div>
  );
}

/**
 * One tile in the Daily Brief count row. Empty (0) tiles dim to 40% so
 * the active counts pop against them — same data, faster visual scan.
 */
function CountTile({ label, value, danger = false }) {
  const isZero = value === 0;
  return (
    <div
      className="flex-1 rounded-xl px-2 py-1.5 text-center transition-opacity"
      style={{ background: 'var(--pd-panel-2)', opacity: isZero ? 0.4 : 1 }}
    >
      <div
        className="text-[14px] font-bold tabular-nums"
        style={{ color: danger ? 'var(--pd-crimson)' : 'var(--pd-text)' }}
      >
        <AnimatedNumber value={value} />
      </div>
      <div className="text-[8px] uppercase" style={{ color: 'var(--pd-subtle)' }}>
        {label}
      </div>
    </div>
  );
}

